// Live's caption look (#239, spec docs/specs/editor/captions.md): the project's one, on every
// caption, in live's pixels on live's frame (`LIVE_FRAME`), with #264's cut, place, animation
// and emphasis. Live's default look until a preset or the theme editor changes it. Pure.
import type { CaptionAnimationPreset, CaptionEasing, CaptionScope, FontFamily } from "../../../api/generated/model";
import type { Face } from "../fonts";
import type { Shadow, Stroke } from "../look";
import { shownText, type TextAlign, type TextBackground, type TextCase } from "../text-style";
import { DEFAULT_PARAGRAPH, type Paragraph, type Placement } from "./segment";
import type { CaptionWord } from "./words";

/** A caption's shadow: live's Shadow at an alpha (live's presets' 0.9, 0.4, 0.5). */
export interface CaptionShadow extends Shadow {
  alpha: number;
}

/** A word's pose: its opacity, its scale about its centre, moved (live's pixels, down positive)
 * and turned (degrees). */
export interface CaptionMotion {
  opacity: number;
  scale: number;
  x: number;
  y: number;
  rotation: number;
}

/** The word being spoken: scaled and moved, held, or (`dynamic`) swelling to it and back. */
export interface CaptionActive {
  scale: number;
  y: number;
  dynamic: boolean;
}

/** Live's caption animation (#264): each scope enters from `enterFrom` to `enterTo` as its first
 * word is spoken. `preset` is the Animation list's entry it was made from; null is a preset's own. */
export interface CaptionAnimation {
  preset: CaptionAnimationPreset | null;
  scope: CaptionScope;
  enterFrom: CaptionMotion;
  enterTo: CaptionMotion;
  durationMs: number;
  easing: CaptionEasing;
  active: CaptionActive | null;
}

/** Live's Emphasize: the look of a word the transcript marks positive or negative. */
export interface CaptionTag {
  color: string;
  /** Its own face (live's Custom Font Family); null: the caption's. */
  font: FontFamily | null;
  weight: number;
  italic: boolean;
  sizeRatio: number;
  underline: boolean;
  /** Live's Difference Filter: it inverts what lies under it. */
  difference: boolean;
  animation: CaptionAnimation | null;
}

export interface CaptionStyle {
  font: FontFamily;
  weight: number;
  italic: boolean;
  size: number;
  textCase: TextCase;
  align: TextAlign;
  /** A word spoken or being spoken; null is live's Text Color "−": its letters clear. */
  color: string | null;
  /** A word still to come; null: as `color`. */
  upcomingColor: string | null;
  /** The word being spoken; null: as `color`. */
  activeColor: string | null;
  /** A box behind the word being spoken; null: none. */
  activeBackground: string | null;
  /** Behind each line; null: none. */
  background: TextBackground | null;
  stroke: Stroke | null;
  shadow: CaptionShadow | null;
  /** Between letters, live's pixels. */
  letterSpacing: number;
  /** The line pitch: times live's 1.15 of the size. */
  lineHeight: number;
  underline: boolean;
  overline: boolean;
  strikethrough: boolean;
  positive: CaptionTag | null;
  negative: CaptionTag | null;
}

/** A project's captions: their look, cut, place, animation (null: none) and the preset the look
 * was last picked from (null: none, live's default look). */
export interface CaptionSettings {
  style: CaptionStyle;
  paragraph: Paragraph;
  placement: Placement;
  animation: CaptionAnimation | null;
  presetId: string | null;
  /** Live's Emphasize "Super size no break": captions never wrap, so it is always kept. */
  superSizeNoBreak: boolean;
}

/** Live's default look: Poppins Bold 80 px, black on a white box, the words to come grey. */
export const DEFAULT_CAPTION_STYLE: CaptionStyle = {
  font: "Poppins",
  weight: 700,
  italic: false,
  size: 80,
  textCase: "as_typed",
  align: "center",
  color: "#000000", // raw-color: live's default caption look
  upcomingColor: "#BBB9BD", // raw-color: live's default caption look
  activeColor: null,
  activeBackground: null,
  background: { color: "#FFFFFF", opacity: 1, radius: 16, padX: 32, padY: 24 }, // raw-color: live's default caption look
  stroke: null,
  shadow: null,
  letterSpacing: 0,
  lineHeight: 1,
  underline: false,
  overline: false,
  strikethrough: false,
  positive: null,
  negative: null,
};

/** Live's default: its look, its cut, centred, and no animation to see (its "Immediate"). */
export const DEFAULT_CAPTIONS: CaptionSettings = {
  style: DEFAULT_CAPTION_STYLE,
  paragraph: DEFAULT_PARAGRAPH,
  placement: { horizontal: "center", vertical: "center" },
  animation: null,
  presetId: null,
  superSizeNoBreak: true,
};

/** Live's word gap in a caption: 0.22 of the size, rounded up (live's pixels). */
export function wordGap(style: CaptionStyle): number {
  return Math.ceil(style.size * 0.22);
}

/** The padding round each line: the background's, or live's 8 by 4 with none. */
export function captionPadding(style: CaptionStyle): { x: number; y: number } {
  return style.background === null ? { x: 8, y: 4 } : { x: style.background.padX, y: style.background.padY };
}

/** A word's state at a caption's own time: spoken (its end passed), active, or still to come. */
export type WordState = "spoken" | "active" | "upcoming";

export function wordState(word: { startMs: number; endMs: number }, atMs: number): WordState {
  if (atMs >= word.endMs) return "spoken";
  return atMs >= word.startMs ? "active" : "upcoming";
}

/** The colour a word is drawn in, by its state (live's: an emphasised word's tag colour in every
 * state, else the active colour, else the coming one, else the spoken one). */
export function wordColor(style: CaptionStyle, state: WordState, tag: CaptionTag | null = null): string | null {
  if (tag !== null) return tag.color;
  if (state === "active") return style.activeColor ?? style.color;
  if (state === "upcoming") return style.upcomingColor ?? style.color;
  return style.color;
}

/** An emphasised word's tag (#264), or none. */
export function tagOf(word: CaptionWord, style: CaptionStyle): CaptionTag | null {
  if (word.emphasis === "positive") return style.positive;
  if (word.emphasis === "negative") return style.negative;
  return null;
}

/** A word's face: its tag's own (live's Custom Font Family) or the caption's, with its weight and
 * slant (live's tag draws at its own weight). */
export function faceOf(word: CaptionWord, style: CaptionStyle): Omit<Face, "text"> {
  const tag = tagOf(word, style);
  if (tag === null) return { family: style.font, weight: style.weight, italic: style.italic };
  return { family: tag.font ?? style.font, weight: tag.weight, italic: tag.italic };
}

/** Every face a caption's words are drawn in, each with the words it draws. */
export function captionFaces(words: readonly CaptionWord[], style: CaptionStyle): Face[] {
  const faces = new Map<string, Face>();
  for (const word of words) {
    const face = faceOf(word, style);
    const key = `${face.family}/${face.weight}/${face.italic}`;
    const text = shownText(word.text, style.textCase);
    faces.set(key, { ...face, text: `${faces.get(key)?.text ?? ""} ${text}` });
  }
  return [...faces.values()];
}
